/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable complexity */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadRemoteModule } from '../utils';
import { TUseLoadModules, TSystemTypes } from '../models';

export enum Status {
    LOADING = 'loading',
    NOT_FOUND = 'not_found',
    ERROR = 'error',
    UNKNOWN = 'unknown',
    LOADED = 'loaded',
}

/**
 * hook that takes a module as an argument and returns the status of the loading process and the AppExport
 * @param {TSystemTypes} module a given module to be loaded
 * @returns {TUseLoadModules} returns the **status** of the loader and the exported app as **AppExport**
 */
const useLoadModules = <T>(module: (T & TSystemTypes) | null): TUseLoadModules => {
    // app a ser exposto e o script que será carregado
    const [loadedApp, setLoadedApp]: any = useState(null);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [error, setError]: any = useState(null);

    // Função que carrega o AppExport do modulo compartilhado.
    const getApp = useCallback(async (appPromise: any) => {
        try {
            const response = await appPromise;
            setLoadedApp(response);
        } catch (error) {
            setHasError(true);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // limpa o loadedApp quando o modulo for trocado
    useEffect(() => {
        setLoadedApp(null);
        setHasError(false);
        setError(null);
    }, [module]);

    // Responsável por fazer o loading dos modulos
    useEffect(() => {
        // espera carregamento do script
        if (module) {
            const appPromise = loadRemoteModule({
                remoteEntry: module.url,
                remoteName: module.scope,
                exposedModule: module.app,
            });
            setLoading(true);
            getApp(appPromise);
        }
    }, [module, getApp]);

    // Verifica o status do carregamento
    const loadStatus = useMemo(() => {
        // carregamento de script falhou
        if (hasError) {
            return Status.ERROR;
            // o modulo passado é nulo
        } else if (!module) {
            return Status.NOT_FOUND;
            /**
             * o status será carregando nos seguintes casos:
             * o script ainda está sendo carregado
             * o component (app) ainda não foi extraído e exposto
             *  */
        } else if (isLoading) {
            return Status.LOADING;
            // o script foi carregado e o app extraído e exposto
        } else if (!isLoading && loadedApp) {
            return Status.LOADED;
        }
        // um fallback para caso não caia em nenhum acima
        return Status.UNKNOWN;
    }, [module, isLoading, hasError, loadedApp]);

    return {
        status: loadStatus,
        exposes: loadedApp,
        error: error,
    };
};

export default useLoadModules;
